import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Search } from "lucide-react";

const STATUS_STYLES = {
  "Payment Pending": "bg-slate-100 text-slate-600",
  "Paid": "bg-blue-100 text-blue-800",
  "Consultation Scheduled": "bg-indigo-100 text-indigo-800",
  "In Preparation": "bg-amber-100 text-amber-800",
  "Delivered": "bg-emerald-100 text-emerald-800",
  "Cancelled": "bg-slate-100 text-slate-600",
  "Refund Pending": "bg-orange-100 text-orange-800",
  "Refunded": "bg-orange-100 text-orange-800",
};

export default function AdminOrders() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [product, setProduct] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (product) params.set("product", product);
    params.set("page", page);
    api.get(`/admin/orders?${params}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status, product]);

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Orders & Fulfilment</h1>
          <p className="mt-1 text-sm text-slate-500">{data?.total ?? "…"} orders · manage payment and fulfilment lifecycle</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search order id / name / phone / email" className="text-sm outline-none w-72" />
        </div>
        <select data-testid="orders-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="orders-product-filter" value={product} onChange={(e) => { setProduct(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">All products</option>
          <option value="REGRET_RISK_99">₹99 Report</option>
          <option value="EXPERT_CALL_499">₹499 Expert Call</option>
          <option value="DECISION_PACK_999">₹999 Decision Pack</option>
        </select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="p-3 text-xs font-semibold text-slate-500">Order</th>
              <th className="p-3 text-xs font-semibold text-slate-500">Product</th>
              <th className="p-3 text-xs font-semibold text-slate-500">Student</th>
              <th className="p-3 text-xs font-semibold text-slate-500">Amount</th>
              <th className="p-3 text-xs font-semibold text-slate-500">Payment</th>
              <th className="p-3 text-xs font-semibold text-slate-500">Fulfilment</th>
              <th className="p-3 text-xs font-semibold text-slate-500">Slot</th>
              <th className="p-3 text-xs font-semibold text-slate-500">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></td></tr>
            ) : (data?.orders || []).length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">No orders match your filters.</td></tr>
            ) : (data.orders).map((o) => (
              <tr key={o.orderId} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-3">
                  <Link to={`/admin/orders/${o.orderId}`} data-testid={`order-link-${o.orderId}`} className="font-mono text-xs text-[#0B2A6B] font-semibold">{o.orderId}</Link>
                </td>
                <td className="p-3 text-slate-700">{o.planLabel}</td>
                <td className="p-3">
                  <div className="font-medium text-slate-800">{o.name}</div>
                  <div className="text-xs text-slate-500">{o.phone}</div>
                </td>
                <td className="p-3 font-semibold text-slate-900">₹{o.amount}</td>
                <td className="p-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{o.paymentStatus}</span></td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[o.fulfilmentStatus] || "bg-slate-100 text-slate-600"}`}>{o.fulfilmentStatus || "—"}</span></td>
                <td className="p-3 text-xs text-slate-500">{o.slot ? o.slot.replace("T", " ") : "—"}</td>
                <td className="p-3 text-xs text-slate-400">{(o.createdAt || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
        <span>Page {data?.page ?? 1}</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40">Prev</button>
          <button disabled={data && page * (data.limit || 50) >= (data.total || 0)} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
